import { useMemo } from "react";

import { Extension } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import { styled } from "@linaria/react";
import { langs, LanguageName, loadLanguage } from "@uiw/codemirror-extensions-langs";
import { createTheme } from "@uiw/codemirror-themes";
import CodeMirror from "@uiw/react-codemirror";
import { match } from "ts-pattern";

import { GalaxyTheme } from "../theme/constants";
import { useGalaxyTheme, withTheme } from "../theme/GalaxyTheme";
import { PropsWithTheme, Theme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

export enum CodeEditorVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

export type CodeEditorLanguage = LanguageName;

type CodeEditorProps = {
  /**
   * The content of the code editor.
   */
  content: string;
  /**
   * The placeholder of the code editor.
   */
  placeholder?: string;
  /**
   * The content type of the code editor.
   */
  lang?: CodeEditorLanguage;
  /**
   * Whether the code editor is read only.
   */
  isReadOnly?: boolean;
  /**
   * Whether to show line numbers.
   */
  noLineNumbers?: boolean;
  /**
   * Custom CodeMirror extensions to add to the editor.
   * Use this for template decorations, custom keymaps, etc.
   */
  customExtensions?: Extension[];
  /**
   * Callback fired when the content changes.
   */
  onChange?: (value: string) => void;
  /**
   * Callback fired when Cmd/Ctrl+Enter is pressed.
   */
  onExecute?: () => void;
  /**
   * Border radius for the editor wrapper.
   */
  borderRadius?: string | number;

  /**
   * Whether to show a border around the editor.
   */
  noBorder?: boolean;

  /**
   * Sets the padding inside the code editor content area.
   * Can be a number (for pixels) or a string (for other units).
   */
  padding?: React.CSSProperties["padding"];

  /**
   * The background color variant of the code editor.
   * Defaults to PRIMARY.
   */
  variant?: CodeEditorVariant;
};

const CodeEditorWrapper = withTheme(styled.div<
  PropsWithTheme<{
    $borderRadius?: string | number;
    $noBorder?: boolean;
  }>
>`
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: ${({ $noBorder, theme }) =>
    $noBorder ? "none" : `0.5px solid ${theme.color.border.primary}`};
  border-radius: ${({ $borderRadius }) => getCssSizeValue($borderRadius)};

  & > div {
    flex: 1;
    min-height: 0;
  }

  & .cm-editor {
    width: 100% !important;
    max-width: 100% !important;
    height: 100% !important;
  }

  & .cm-scroller {
    overflow: auto !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  & .cm-content {
    max-width: 100% !important;
  }

  & .cm-content,
  & .cm-gutter {
    line-height: 1.8;
  }
`);

function getBackgroundColorFromVariant(theme: Theme, variant: CodeEditorVariant) {
  return match(variant)
    .with(CodeEditorVariant.BASE, () => theme.color.background.base)
    .with(CodeEditorVariant.PRIMARY, () => theme.color.background.primary)
    .with(CodeEditorVariant.SECONDARY, () => theme.color.background.secondary)
    .with(CodeEditorVariant.TERTIARY, () => theme.color.background.tertiary)
    .exhaustive();
}

export function createSearchPanelThemeExtension(theme: Theme) {
  return EditorView.theme({
    ".cm-panels": {
      backgroundColor: "transparent",
    },
    ".cm-panels.cm-panels-top": {
      borderBottom: "none",
    },
    ".cm-panels.cm-panels-bottom": {
      borderTop: "none",
    },
    ".cm-panel.cm-search": {
      margin: "6px",
      padding: "6px",
      width: "fit-content",
      maxWidth: "calc(100% - 12px)",
      borderRadius: "5px",
      border: `0.5px solid ${theme.color.border.primary}`,
      backgroundColor: theme.color.background.primary,
      color: theme.color.text.primary,
      fontFamily: theme.font.sans.family,
      fontSize: theme.font.sans.size.body_md,
    },
    ".cm-panel.cm-search input, .cm-panel.cm-search button, .cm-panel.cm-search label": {
      margin: 0,
    },
    ".cm-panel.cm-search .cm-textfield[name=replace], .cm-panel.cm-search .cm-button[name=replace], .cm-panel.cm-search .cm-button[name=replaceAll], .cm-panel.cm-search label:has(input[name=case]), .cm-panel.cm-search label:has(input[name=re]), .cm-panel.cm-search label:has(input[name=word]), .cm-panel.cm-search [name=close], .cm-panel.cm-search br":
      {
        display: "none",
      },
    ".cm-panel.cm-search .cm-textfield": {
      height: "24px",
      minHeight: "24px",
      maxHeight: "24px",
      padding: "0 6px",
      margin: 0,
      boxSizing: "border-box",
      alignSelf: "center",
      verticalAlign: "middle",
      borderRadius: "5px",
      border: `0.5px solid ${theme.color.border.primary}`,
      background: theme.color.background.primary,
      color: theme.color.text.primary,
      fontFamily: theme.font.sans.family,
      fontSize: theme.font.sans.size.body_md,
      lineHeight: "24px",
      fontWeight: theme.font.sans.weight.regular,
      outline: "none",
      "&::placeholder": {
        color: theme.color.text.tertiary,
      },
      "&:focus": {
        borderColor: theme.color.border.selected,
      },
    },
    ".cm-panel.cm-search .cm-button": {
      height: "24px",
      minHeight: "24px",
      maxHeight: "24px",
      padding: "0 10px",
      margin: 0,
      boxSizing: "border-box",
      alignSelf: "center",
      borderRadius: "5px",
      border: "0.5px solid transparent",
      background: "transparent",
      backgroundImage: "none",
      color: theme.color.text.primary,
      fontFamily: theme.font.sans.family,
      fontSize: theme.font.sans.size.body_md,
      lineHeight: "24px",
      fontWeight: theme.font.sans.weight.medium,
      textTransform: "capitalize",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
      appearance: "none",
      WebkitAppearance: "none",
      boxShadow: "none",
      filter: "none",
      transition: "background-color 0.075s ease-in-out, border 0.075s ease-in-out",
      "&:hover": {
        backgroundColor: theme.color.background.secondary,
        borderColor: theme.color.border.primary,
      },
    },
  });
}

export function createCodeMirrorThemeExtension(
  theme: Theme,
  galaxyTheme: Exclude<GalaxyTheme, GalaxyTheme.SYSTEM>,
  variant: CodeEditorVariant,
  isReadOnly?: boolean,
) {
  const codeMirrorTheme = match(galaxyTheme)
    .returnType<"dark" | "light">()
    .with(GalaxyTheme.DARK, () => "dark")
    .with(GalaxyTheme.LIGHT, () => "light")
    .exhaustive();

  const settingsBackgroundColor = getBackgroundColorFromVariant(theme, variant);
  const settingsSelectionColor = isReadOnly ? theme.color.opacity.bw0 : theme.color.opacity.bw16Alt;
  const settingsSelectionMatchColor = getBackgroundColorFromVariant(theme, variant);
  const settingsGutterBackgroundColor = getBackgroundColorFromVariant(theme, variant);
  const settingsGutterActiveForegroundColor = isReadOnly
    ? theme.color.text.tertiary
    : theme.color.text.primary;

  return createTheme({
    theme: codeMirrorTheme,
    settings: {
      background: settingsBackgroundColor,
      foreground: theme.color.text.primary,
      caret: theme.color.text.primary,
      selection: settingsSelectionColor,
      selectionMatch: settingsSelectionMatchColor,
      lineHighlight: "transparent",
      gutterBackground: settingsGutterBackgroundColor,
      gutterForeground: theme.color.text.tertiary,
      gutterActiveForeground: settingsGutterActiveForegroundColor,
      gutterBorder: "transparent",
      fontFamily: theme.font.mono.family,
      fontSize: theme.font.mono.size.body_md,
    },
    styles: [
      // Comments
      { tag: t.comment, color: theme.color.text.tertiary, fontStyle: "italic" },
      { tag: t.lineComment, color: theme.color.text.tertiary, fontStyle: "italic" },
      { tag: t.blockComment, color: theme.color.text.tertiary, fontStyle: "italic" },

      // Variables & identifiers
      { tag: t.variableName, color: theme.color.text.primary },
      { tag: t.definition(t.variableName), color: theme.color.text.primary },

      // JSON keys
      { tag: t.propertyName, color: theme.color.text.purple },
      { tag: t.definition(t.propertyName), color: theme.color.text.purple },

      // Strings (JSON values)
      { tag: t.string, color: theme.color.text.primary },
      { tag: t.special(t.string), color: theme.color.text.primary },
      { tag: t.character, color: theme.color.text.primary },
      { tag: t.escape, color: theme.color.text.primary },

      // Numbers & constants
      { tag: t.number, color: theme.color.text.pink },
      { tag: t.integer, color: theme.color.text.pink },
      { tag: t.float, color: theme.color.text.pink },

      // Booleans & null
      { tag: t.bool, color: theme.color.text.pink },
      { tag: t.null, color: theme.color.text.pink },
      { tag: t.atom, color: theme.color.text.pink },

      // Keywords
      { tag: t.keyword, color: theme.color.text.blue, fontWeight: "500" },
      { tag: t.modifier, color: theme.color.text.blue },
      { tag: t.self, color: theme.color.text.blue },

      // Operators & punctuation
      { tag: t.operator, color: theme.color.text.tertiary },
      { tag: t.punctuation, color: theme.color.text.tertiary },
      { tag: t.separator, color: theme.color.text.tertiary },
      { tag: t.bracket, color: theme.color.text.tertiary },
      { tag: t.angleBracket, color: theme.color.text.tertiary },
      { tag: t.squareBracket, color: theme.color.text.tertiary },
      { tag: t.paren, color: theme.color.text.tertiary },
      { tag: t.brace, color: theme.color.text.tertiary },
      { tag: t.special(t.brace), color: theme.color.text.teal },

      // Types & classes
      { tag: t.className, color: theme.color.text.purple },
      { tag: t.typeName, color: theme.color.text.purple },
      { tag: t.definition(t.typeName), color: theme.color.text.purple },
      { tag: t.namespace, color: theme.color.text.teal },

      // Functions
      { tag: t.function(t.variableName), color: theme.color.text.blue },
      { tag: t.definition(t.function(t.variableName)), color: theme.color.text.blue },
      { tag: t.function(t.propertyName), color: theme.color.text.teal },

      // HTML/JSX tags
      { tag: t.tagName, color: theme.color.text.pink },
      { tag: t.attributeName, color: theme.color.text.blue },
      { tag: t.attributeValue, color: theme.color.text.green },

      // Markdown & documentation
      { tag: t.heading, color: theme.color.text.pink, fontWeight: "bold" },
      { tag: t.heading1, color: theme.color.text.pink, fontWeight: "bold" },
      { tag: t.heading2, color: theme.color.text.orange, fontWeight: "bold" },
      { tag: t.heading3, color: theme.color.text.teal, fontWeight: "bold" },
      { tag: t.link, color: theme.color.text.blue, textDecoration: "underline" },
      { tag: t.url, color: theme.color.text.teal, textDecoration: "underline" },
      { tag: t.emphasis, color: theme.color.text.lime, fontStyle: "italic" },
      { tag: t.strong, color: theme.color.text.purple, fontWeight: "bold" },
      { tag: t.strikethrough, color: theme.color.text.tertiary, textDecoration: "line-through" },

      // Regex
      { tag: t.regexp, color: theme.color.text.yellow },

      // Labels & meta
      { tag: t.labelName, color: theme.color.text.lime },
      { tag: t.meta, color: theme.color.text.tertiary },
      { tag: t.annotation, color: theme.color.text.tertiary },
      { tag: t.processingInstruction, color: theme.color.text.tertiary },

      // Invalid/error
      { tag: t.invalid, color: theme.color.text.error },
    ],
  });
}

const CodeEditor = ({
  content,
  placeholder,
  lang,
  customExtensions = [],
  onChange,
  onExecute,
  borderRadius,
  noBorder,
  isReadOnly,
  noLineNumbers,
  padding,
  variant = CodeEditorVariant.PRIMARY,
}: CodeEditorProps) => {
  const { theme, galaxyTheme } = useGalaxyTheme();

  const codeMirrorTheme = useMemo(
    () => createCodeMirrorThemeExtension(theme, galaxyTheme, variant, isReadOnly),
    [theme, galaxyTheme, variant, isReadOnly],
  );

  const langExtensions = useMemo(() => {
    if (!lang) {
      return [];
    }
    void loadLanguage(lang);
    const langExtension = langs[lang];
    if (langExtension) return [langExtension()];
    return [];
  }, [lang]);

  const keymapExtensions = useMemo(() => {
    if (!onExecute) {
      return [];
    }
    return [
      keymap.of([
        {
          key: "Mod-Enter",
          run: () => {
            onExecute();
            return true;
          },
        },
      ]),
    ];
  }, [onExecute]);

  const searchPanelTheme = useMemo(() => createSearchPanelThemeExtension(theme), [theme]);

  const noLineNumbersTheme = useMemo(() => {
    if (!noLineNumbers) {
      return [];
    }
    return [
      EditorView.theme({
        ".cm-gutters": {
          display: "none",
        },
      }),
    ];
  }, [noLineNumbers]);

  const paddingTheme = useMemo(() => {
    return [
      EditorView.theme({
        ".cm-content": {
          padding: getCssSizeValue(padding ?? 4),
        },
      }),
    ];
  }, [padding]);

  const extensions = useMemo(() => {
    return [
      ...langExtensions,
      ...keymapExtensions,
      searchPanelTheme,
      ...noLineNumbersTheme,
      ...paddingTheme,
      ...customExtensions,
    ];
  }, [
    langExtensions,
    keymapExtensions,
    searchPanelTheme,
    noLineNumbersTheme,
    paddingTheme,
    customExtensions,
  ]);

  return (
    <CodeEditorWrapper $borderRadius={borderRadius} $noBorder={noBorder}>
      <CodeMirror
        value={content}
        placeholder={placeholder}
        width="100%"
        height="100%"
        theme={codeMirrorTheme}
        editable={!isReadOnly}
        basicSetup={{
          lineNumbers: !noLineNumbers,
        }}
        extensions={extensions}
        onChange={onChange}
      />
    </CodeEditorWrapper>
  );
};

export default CodeEditor;
